import { useTranslation } from "@/contexts/LanguageContext";
import {
  Aperture,
  Lightning,
  Backpack,
  Crosshair,
  Lightbulb,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const IDEAL_FOCAL = {
  portrait: { min: 50, max: 85, type: "standard" },
  landscape: { min: 16, max: 35, type: "wide" },
  sports: { min: 70, max: 200, type: "telephoto" },
  wildlife: { min: 100, max: 400, type: "telephoto" },
  macro: { min: 50, max: 100, type: "macro" },
  street: { min: 28, max: 50, type: "standard" },
  product: { min: 50, max: 100, type: "standard" },
  event_indoor: { min: 24, max: 70, type: "standard" },
  general: { min: 35, max: 70, type: "standard" },
};

function scoreLens(lens, photoType, recAperture) {
  const ideal = IDEAL_FOCAL[photoType] || IDEAL_FOCAL.general;
  let score = 0;
  const fMin = lens.focal_min;
  const fMax = lens.focal_max || lens.focal_min;
  const isPrime = fMin === fMax;

  // Focal length coverage
  if (fMin <= ideal.min && fMax >= ideal.max) score += 4;
  else if (fMin <= ideal.max && fMax >= ideal.min) score += 2;
  else if (Math.abs(fMin - ideal.min) < 20 || Math.abs(fMax - ideal.max) < 20)
    score += 1;

  // Portrait: strong prime bonus at classic portrait lengths
  if (photoType === "portrait") {
    if (isPrime && (fMin === 85 || fMin === 50)) score += 4;
    else if (isPrime && fMin >= 35 && fMin <= 100) score += 2;
    if (fMin <= 85 && fMax >= 85) score += 1;
  }

  // Sports/wildlife: reach bonus
  if (["sports", "wildlife"].includes(photoType)) {
    if (fMax >= 200) score += 3;
    if (fMax >= 300) score += 1;
  }

  // Landscape: ultra-wide bonus
  if (photoType === "landscape") {
    if (fMin <= 24) score += 3;
    if (fMin <= 16) score += 1;
  }

  // Macro: dedicated lens strongly preferred
  if (photoType === "macro" && lens.lens_type === "macro") score += 5;

  // Aperture (wider = better for light gathering)
  if (lens.max_aperture <= recAperture) score += 3;
  else if (lens.max_aperture <= recAperture + 1) score += 1;

  // Lens type match
  if (lens.lens_type === ideal.type) score += 2;

  return score;
}

function getLensReason(lens, photoType, t) {
  const isPrime = lens.focal_min === (lens.focal_max || lens.focal_min);
  const fMax = lens.focal_max || lens.focal_min;

  if (photoType === "portrait") {
    if (isPrime && lens.max_aperture <= 2) return t("gr_reason_portrait_prime");
    if (lens.focal_min <= 85 && fMax >= 50) return t("gr_reason_portrait_zoom");
  }
  if (["sports", "wildlife"].includes(photoType)) {
    if (fMax >= 200) return t("gr_reason_sports_tele");
    return t("gr_reason_sports_reach");
  }
  if (photoType === "landscape") {
    if (lens.focal_min <= 24) return t("gr_reason_landscape_wide");
    return t("gr_reason_landscape_general");
  }
  if (photoType === "macro" && lens.lens_type === "macro")
    return t("gr_reason_macro");
  if (lens.max_aperture <= 2) return t("gr_reason_fast_aperture");
  return t("gr_reason_versatile");
}

function getSuggestions(gear, results, t) {
  const suggestions = [];
  const photoType = results.photo_type;
  const hasLensType = (type) => gear.lenses?.some((l) => l.lens_type === type);
  const ev = results.effective_ev;
  const hasFastPrime = gear.lenses?.some(
    (l) => l.max_aperture <= 2 && l.focal_min === (l.focal_max || l.focal_min)
  );

  if (["sports", "wildlife"].includes(photoType) && !hasLensType("telephoto")) {
    suggestions.push(t("gr_suggest_telephoto"));
  }
  if (photoType === "landscape" && !hasLensType("wide")) {
    suggestions.push(t("gr_suggest_wide"));
  }
  if (photoType === "portrait" && !hasFastPrime) {
    suggestions.push(t("gr_suggest_portrait_prime"));
  }
  if (photoType === "macro" && !hasLensType("macro")) {
    suggestions.push(t("gr_suggest_macro"));
  }
  if (ev < 8 && !hasFastPrime && photoType !== "portrait") {
    suggestions.push(t("gr_suggest_fast_prime"));
  }
  if (ev < 8 && !gear.lighting?.some(l => l === "speedlight" || l?.item_id === "speedlight")) {
    suggestions.push(t("gr_suggest_speedlight"));
  }
  if (
    results.warnings?.includes("tripod_recommended") &&
    !gear.support?.some(s => s === "tripod" || s?.item_id === "tripod")
  ) {
    suggestions.push(t("gr_suggest_tripod"));
  }
  if (
    ["sports", "wildlife"].includes(photoType) &&
    gear.lenses?.some((l) => (l.focal_max || l.focal_min) >= 200) &&
    !gear.support?.includes("monopod")
  ) {
    suggestions.push(t("gr_suggest_monopod"));
  }
  return suggestions.slice(0, 2);
}

export default function GearRecommendation({ results, gear, isAuthenticated }) {
  const { t } = useTranslation();

  if (!results) return null;

  const hasGear =
    (gear?.cameras?.length || 0) +
    (gear?.lenses?.length || 0) +
    (gear?.lighting?.length || 0) +
    (gear?.support?.length || 0);

  // Empty-state for authenticated users with no gear
  if (!hasGear && isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-4 bg-[#121214]/80 backdrop-blur-md border border-dashed border-white/10 rounded-xl p-5 flex items-start gap-3"
        data-testid="gear-empty-state"
      >
        <Crosshair weight="regular" className="w-5 h-5 text-[#52525B] shrink-0 mt-0.5" />
        <div>
          <h4 className="text-white font-medium text-sm mb-1">{t("gr_title")}</h4>
          <p className="text-[#71717A] text-sm">{t("gr_empty_state")}</p>
        </div>
      </motion.div>
    );
  }

  if (!hasGear) return null;

  const photoType = results.photo_type;
  const recAp = results.recommended.aperture_value;

  // Find best lens
  let bestLens = null;
  if (gear.lenses?.length) {
    const scored = gear.lenses
      .map((l) => ({ lens: l, score: scoreLens(l, photoType, recAp) }))
      .sort((a, b) => b.score - a.score);
    if (scored[0]?.score > 0) bestLens = scored[0].lens;
  }

  // Lighting recommendation
  const needsFlash = results.effective_ev < 8;
  const hasSpeedlight = gear.lighting?.some(l => l === "speedlight" || l?.item_id === "speedlight");
  const hasLed = gear.lighting?.some(l => l === "led_panel" || l?.item_id === "led_panel");

  // Support recommendation
  const needsTripod = results.warnings?.some(
    (w) => w === "tripod_recommended" || w === "tripod_would_help"
  );
  const hasTripod = gear.support?.some(s => s === "tripod" || s?.item_id === "tripod");
  const hasMonopod = gear.support?.some(s => s === "monopod" || s?.item_id === "monopod");

  // Smart suggestions
  const suggestions = getSuggestions(gear, results, t);

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-3 mt-4"
      data-testid="gear-recommendation"
    >
      <motion.div variants={item} className="flex items-center gap-2">
        <Crosshair weight="fill" className="w-4.5 h-4.5 text-[#F5A524]" />
        <h2 className="font-['Outfit'] text-xl font-semibold text-white">
          {t("gr_title")}
        </h2>
      </motion.div>

      {/* Best Lens */}
      <motion.div
        variants={item}
        className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-4"
        data-testid="gr-best-lens"
      >
        <div className="flex items-start gap-3">
          <Aperture weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-[#F5A524]" />
          <div className="flex-1">
            <h4 className="text-white font-medium text-sm mb-1">{t("gr_best_lens")}</h4>
            {bestLens ? (
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-emerald-400 text-sm font-medium">
                    {bestLens.name}
                  </span>
                </div>
                <p className="text-[#71717A] text-xs mt-1">
                  {bestLens.focal_min === bestLens.focal_max
                    ? `${bestLens.focal_min}mm`
                    : `${bestLens.focal_min}-${bestLens.focal_max}mm`}
                  {" "}f/{bestLens.max_aperture} - {t(`lens_${bestLens.lens_type}`)}
                </p>
                <p className="text-[#A1A1AA] text-xs mt-1.5 leading-relaxed" data-testid="gr-lens-reason">
                  {getLensReason(bestLens, photoType, t)}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <WarningCircle weight="fill" className="w-3.5 h-3.5 text-[#F5A524] shrink-0" />
                <span className="text-[#A1A1AA] text-sm">{t("gr_no_lens_match")}</span>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Lighting */}
      {(needsFlash || hasSpeedlight || hasLed) && (
        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-4"
          data-testid="gr-lighting"
        >
          <div className="flex items-start gap-3">
            <Lightning weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
            <div>
              <h4 className="text-white font-medium text-sm mb-1">{t("gr_lighting_rec")}</h4>
              {needsFlash && hasSpeedlight && (
                <div className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-emerald-400 text-sm">{t("gr_use_speedlight")}</span>
                </div>
              )}
              {needsFlash && !hasSpeedlight && hasLed && (
                <div className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span className="text-blue-400 text-sm">{t("gr_use_led")}</span>
                </div>
              )}
              {!needsFlash && (hasSpeedlight || hasLed) && (
                <span className="text-[#A1A1AA] text-sm">{t("gr_no_flash_needed")}</span>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Support Gear */}
      {(needsTripod || hasTripod || hasMonopod) && (
        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-4"
          data-testid="gr-support"
        >
          <div className="flex items-start gap-3">
            <Backpack weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-blue-400" />
            <div>
              <h4 className="text-white font-medium text-sm mb-1">{t("gr_support_rec")}</h4>
              {needsTripod && hasTripod && (
                <div className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-emerald-400 text-sm">{t("gr_use_tripod")}</span>
                </div>
              )}
              {needsTripod && !hasTripod && hasMonopod && (
                <div className="flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                  <span className="text-blue-400 text-sm">{t("gr_use_monopod")}</span>
                </div>
              )}
              {!needsTripod && (
                <span className="text-[#A1A1AA] text-sm">{t("gr_no_support_needed")}</span>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Smart Suggestions */}
      {suggestions.length > 0 && (
        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/20 rounded-xl p-4"
          data-testid="gr-suggestions"
        >
          <div className="flex items-start gap-3">
            <Lightbulb weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-[#F5A524]" />
            <div>
              <h4 className="text-white font-medium text-sm mb-2">{t("gr_missing")}</h4>
              <div className="space-y-1.5">
                {suggestions.map((s, i) => (
                  <p key={i} className="text-[#A1A1AA] text-sm flex items-start gap-2">
                    <span className="text-[#F5A524] mt-0.5 shrink-0">+</span>
                    {s}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
